/**
 * Formulário de uma ausência, embutido na coluna ao lado da grade.
 *
 * Papel: registra o período em que alguém não está disponível para a escala,
 * com a pessoa, as duas pontas e o motivo opcional, num bloco cinza que abre
 * entre a lista de férias e o botão "+ Adicionar férias".
 *
 * Como usar: quem monta este componente passa `ausencia` para editar ou `null`
 * para criar, e deverá dar a ele uma `key` diferente por registro. Sem a `key`,
 * o React reaproveita a mesma instância ao trocar de ausência e os campos
 * continuam com o que a pessoa via antes. O botão de gravar diz "Confirmar" na
 * criação e "Atualizar" na edição.
 *
 * Quem tem a ausência em edição e não está mais em `pessoas` — saiu da escala
 * depois dela — entra nas opções do campo "Funcionário" depois das demais. Sem
 * isso, a escolha não teria item correspondente e o campo apareceria vazio,
 * sem nem o texto de escolher, e quem edita poderia trocar a pessoa sem
 * perceber que o registro já tinha uma.
 *
 * O período é fechado nas duas pontas: quem volta no dia 14 tem `fim` no dia 13.
 * Um dia só se escreve com as duas datas iguais, e é por isso que a validação
 * aceita igualdade em vez de exigir que o fim seja posterior.
 */
import { type Ausencia, novaAusenciaSchema } from '@concierge/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, type FieldPath, useForm } from 'react-hook-form';

import { aplicarErroDaApi, tradutorPorMapa } from '@/shared/lib/erro-de-formulario';
import { Button } from '@/shared/ui/button';
import { CampoMascarado } from '@/shared/ui/campo-mascarado';
import { Label } from '@/shared/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/textarea';
import { useSalvarAusencia } from '../hooks/usar-calendario';
import { AUSENCIA_EM_BRANCO, type AusenciaNoFormulario, type PessoaDaEscala } from '../types';
import { CAMPO_EMBUTIDO, FORMULARIO_EMBUTIDO, ROTULO_EMBUTIDO } from './estilo-embutido';

interface FormularioAusenciaProps {
  /** A ausência a editar, ou `null` para criar uma nova. */
  ausencia: Ausencia | null;
  /** Quem está na escala da casa, que é quem pode ficar ausente. */
  pessoas: PessoaDaEscala[];
  onSalvo: () => void;
  onCancelar: () => void;
}

/** Os campos que a API pode apontar, cada um no caminho do formulário. */
const CAMPOS: Record<string, FieldPath<AusenciaNoFormulario> | undefined> = {
  colaboradorId: 'colaboradorId',
  inicio: 'inicio',
  fim: 'fim',
  observacao: 'observacao',
};

/**
 * O formulário de uma ausência.
 *
 * @param ausencia - O registro a editar, ou `null`.
 * @param pessoas - Quem está na escala da casa, na ordem da coluna; a criação
 *   começa na primeira pessoa, e quem tem a ausência em edição e não está aqui
 *   é acrescentado depois deles.
 * @param onSalvo - Chamado depois de gravar, para fechar o bloco.
 * @param onCancelar - Chamado ao desistir, sem gravar nada.
 * @returns O bloco do formulário, com a pessoa, o período e o motivo.
 */
export function FormularioAusencia({
  ausencia,
  pessoas,
  onSalvo,
  onCancelar,
}: FormularioAusenciaProps) {
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const salvar = useSalvarAusencia();

  const form = useForm<AusenciaNoFormulario>({
    resolver: zodResolver(novaAusenciaSchema),
    defaultValues:
      ausencia === null
        ? { ...AUSENCIA_EM_BRANCO, colaboradorId: pessoas[0]?.id ?? '' }
        : {
            colaboradorId: ausencia.colaborador.id,
            inicio: ausencia.inicio,
            fim: ausencia.fim,
            observacao: ausencia.observacao ?? '',
          },
  });

  const enviando = salvar.isPending;

  const opcoes = pessoas.map((p) => ({ id: p.id, nome: p.apelido ?? p.nomeCompleto }));
  if (ausencia !== null && !pessoas.some((p) => p.id === ausencia.colaborador.id)) {
    const { colaborador } = ausencia;
    opcoes.push({ id: colaborador.id, nome: colaborador.apelido ?? colaborador.nomeCompleto });
  }

  const handleSalvar = form.handleSubmit(async (dados) => {
    setErroGeral(null);
    try {
      await salvar.mutateAsync({
        id: ausencia?.id ?? null,
        dados: novaAusenciaSchema.parse(dados),
      });
      onSalvo();
    } catch (erro) {
      setErroGeral(aplicarErroDaApi(erro, form, tradutorPorMapa(CAMPOS)));
    }
  });

  return (
    <form
      aria-label={ausencia === null ? 'Novas férias' : 'Editar férias'}
      onSubmit={(evento) => void handleSalvar(evento)}
      className={FORMULARIO_EMBUTIDO}
    >
      <div>
        <Label htmlFor="ausencia-pessoa" className={ROTULO_EMBUTIDO}>
          Funcionário
        </Label>
        <Controller
          control={form.control}
          name="colaboradorId"
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange} disabled={enviando}>
              <SelectTrigger id="ausencia-pessoa" className={CAMPO_EMBUTIDO}>
                <SelectValue placeholder="Escolha a pessoa" />
              </SelectTrigger>
              <SelectContent>
                {opcoes.map((opcao) => (
                  <SelectItem key={opcao.id} value={opcao.id}>
                    {opcao.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {form.formState.errors.colaboradorId !== undefined && (
          <p className="mt-1 text-xs text-perigo">
            {form.formState.errors.colaboradorId.message}
          </p>
        )}
      </div>

      <div className="flex gap-2 [&>*]:min-w-0 [&>*]:flex-1">
        <div>
          <Label htmlFor="ausencia-inicio" className={ROTULO_EMBUTIDO}>
            Início
          </Label>
          <Controller
            control={form.control}
            name="inicio"
            render={({ field }) => (
              <CampoMascarado
                id="ausencia-inicio"
                formato="data"
                value={field.value}
                onChange={field.onChange}
                disabled={enviando}
                className={CAMPO_EMBUTIDO}
              />
            )}
          />
          {form.formState.errors.inicio !== undefined && (
            <p className="mt-1 text-xs text-perigo">{form.formState.errors.inicio.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="ausencia-fim" className={ROTULO_EMBUTIDO}>
            Fim
          </Label>
          <Controller
            control={form.control}
            name="fim"
            render={({ field }) => (
              <CampoMascarado
                id="ausencia-fim"
                formato="data"
                value={field.value}
                onChange={field.onChange}
                disabled={enviando}
                className={CAMPO_EMBUTIDO}
              />
            )}
          />
          {form.formState.errors.fim !== undefined && (
            <p className="mt-1 text-xs text-perigo">{form.formState.errors.fim.message}</p>
          )}
        </div>
      </div>

      <div>
        <Label htmlFor="ausencia-observacao" className={ROTULO_EMBUTIDO}>
          Motivo (opcional)
        </Label>
        <Controller
          control={form.control}
          name="observacao"
          render={({ field }) => (
            <Textarea
              id="ausencia-observacao"
              rows={1}
              {...field}
              value={field.value ?? ''}
              disabled={enviando}
              className={CAMPO_EMBUTIDO}
            />
          )}
        />
      </div>

      {erroGeral !== null && <p className="text-xs text-perigo">{erroGeral}</p>}

      <div className="flex gap-2">
        <Button type="submit" disabled={enviando} className="flex-1">
          {enviando ? 'Salvando…' : ausencia === null ? 'Confirmar' : 'Atualizar'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancelar} disabled={enviando}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}
